import { formatGameTime } from '../lib/gameTime';
import { useI18n } from '../i18n/context';
import { LOCALES, LOCALE_IDS } from '../i18n';
import { MAP_DEFS } from '../content/maps';
import type { MapId } from '../content/maps';
import { DIFFICULTY_DEFS } from '../content/difficulties';
import type { DifficultyId } from '../content/difficulties';
import type { SaveSlot } from '../app/storage';

interface MainMenuProps {
    readonly saveSlot: SaveSlot | null;
    readonly difficulty: DifficultyId;
    readonly onDifficulty: (difficulty: DifficultyId) => void;
    readonly onNewGame: (mapId: MapId, players: number) => void;
    readonly onContinue: () => void;
    /**
     * האם הבנייה הזו יודעת בכלל לאן להתחבר (‏`VITE_RELAY_URL`).
     *
     * **הכפתור מוצג תמיד ומושבת כשאין שרת, ולא נעלם** — מלכודת 27. וגם: "אין שרת"
     * הוא מידע שימושי בבנייה שנפרסה לפני שה-Worker עלה, שזה המצב הצפוי.
     */
    readonly multiplayerAvailable: boolean;
    readonly onMultiplayer: () => void;
}

export function MainMenu({
    saveSlot,
    difficulty,
    onDifficulty,
    onNewGame,
    onContinue,
    multiplayerAvailable,
    onMultiplayer,
}: MainMenuProps) {
    const { t, locale, cycleLocale } = useI18n();

    const nextLocale = LOCALE_IDS[(LOCALE_IDS.indexOf(locale) + 1) % LOCALE_IDS.length];

    return (
        <div className="menu">
            <div className="menu__brand">
                <img className="menu__mark" src="/icon.svg" alt="" />
                <h1 className="menu__title">{t('app_name')}</h1>
                <p className="menu__tagline">{t('app_tagline')}</p>
            </div>

            {/*
                רמת קושי חדשה בתוכן מופיעה כאן מעצמה, בדיוק כמו מפה חדשה.
                הבחירה נשמרת במצב המשחק עצמו, ולכן "המשך משחק" חוזר לאותו יריב.
            */}
            <div className="menu__difficulty" role="group" aria-label={t('menu_difficulty')}>
                <span className="menu__difficultyLabel">{t('menu_difficulty')}</span>
                <div className="menu__difficultyOptions">
                    {DIFFICULTY_DEFS.map((def, index) => (
                        <button
                            key={def.id}
                            className={
                                index === difficulty
                                    ? 'menu__chip menu__chip--on'
                                    : 'menu__chip'
                            }
                            type="button"
                            aria-pressed={index === difficulty}
                            onClick={() => onDifficulty(index)}
                        >
                            {t(def.nameKey)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="menu__actions">
                {/*
                    מפה חדשה בתוכן מופיעה כאן מעצמה — אין רשימה שנייה לתחזק.

                    **שם המפה הוא הכותרת, לא "משחק חדש".** כשהיו שתי מפות זה עבד;
                    עם שש נוצרו שישה כפתורים זהים בגודל מלא, ומה שמבדיל ביניהם היה
                    שורה אפורה קטנה. הכותרת צריכה להיות מה שבאמת שונה בין הפריטים.
                */}
                <div className="menu__maps" role="group" aria-label={t('menu_new_game')}>
                    {MAP_DEFS.map((map) => (
                        <button
                            key={map.id}
                            className="menu__map"
                            type="button"
                            /*
                                מפה קובעת כמה שחקנים היא מכילה — מספר משבצות
                                הפתיחה. **אין בורר נפרד** שאפשר להצליב לא נכון
                                עם מפה: מפת ארבעה היא משחק של ארבעה, נקודה.
                            */
                            onClick={() => onNewGame(map.id, map.startSlots.length)}
                        >
                            <span className="menu__mapName">{t(map.nameKey)}</span>
                            {/* הגודל קובע כמה זמן יש לבנות לפני שנפגשים. */}
                            <span className="menu__buttonNote num">
                                {map.w}×{map.h}
                                {map.startSlots.length > 2 ? ` · ${map.startSlots.length}` : ''}
                            </span>
                        </button>
                    ))}
                </div>
                <button
                    className="menu__button"
                    type="button"
                    onClick={onContinue}
                    disabled={!saveSlot}
                >
                    <span>{t('menu_continue')}</span>
                    <span className="menu__buttonNote">
                        {saveSlot ? (
                            <span className="num">{formatGameTime(saveSlot.game.tick)}</span>
                        ) : (
                            t('menu_continue_empty')
                        )}
                    </span>
                </button>
                <button
                    className="menu__button"
                    type="button"
                    onClick={onMultiplayer}
                    disabled={!multiplayerAvailable}
                >
                    <span>{t('menu_multiplayer')}</span>
                    <span className="menu__buttonNote">
                        {multiplayerAvailable
                            ? t('menu_multiplayer_note')
                            : t('lobby_error_no_server')}
                    </span>
                </button>
                <button className="menu__button" type="button" disabled>
                    <span>{t('menu_settings')}</span>
                    <span className="menu__buttonNote">{t('menu_coming_soon')}</span>
                </button>
            </div>

            <p className="menu__notice">{t('milestone_notice')}</p>

            <div className="menu__footer">
                <button
                    className="menu__lang"
                    type="button"
                    onClick={cycleLocale}
                    aria-label={t('lang_switch')}
                >
                    {nextLocale ? LOCALES[nextLocale].name : ''}
                </button>
                {/*
                    חותמת הבנייה היא הפוסק היחיד בין "לא נפרס" לבין "נפרס אבל המטמון ישן".
                    לחיצה טוענת מחדש, כדי שאפשר יהיה לנסות לרענן ישר מהמסך.
                */}
                <button
                    className="menu__stamp"
                    type="button"
                    onClick={() => window.location.reload()}
                    title={t('build_reload_hint')}
                >
                    {t('build_version_label')} <span className="num">{__BUILD_ID__}</span>
                </button>
            </div>
        </div>
    );
}
